/* import __COLOCATED_TEMPLATE__ from './creator-card.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import clsx from 'clsx';

const CLASS_NAME = 'SH-c-people-creator-card';
const CLASS_NAME_MEDIA_HOVERED = `${CLASS_NAME}--media-hovered`;
const CLASS_NAME_NO_ADDITIONAL_INFO = `${CLASS_NAME}--no-additional-info`;
const CLASS_NAME_NO_DIGESTS = `${CLASS_NAME}--no-digests`;

const WORKED_WITH_BRAND_LOGO_SIZE_PX = 40;

class PeopleCreatorCard extends Component {
  @service('state-managers/application') applicationStateManager;
  @service('commands/company-connection-commands') companyConnectionCommands;
  @service('error-tracker') errorTracker;
  @service media;
  @service moment;
  @service session;
  @service('commands/user-commands') userCommandsService;

  /** @var {Boolean} mediaIsHovered whether media is being hovered */
  @tracked mediaIsHovered = false;

  /** @var {Boolean} digestThumbnailError whether image failed to load */
  @tracked digestThumbnailError = false;

  /** @var {Boolean} isSaved whether creator is saved or being saved */
  @tracked isSaved = Boolean(this.args.user.inCurrentUsersNetwork);

  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME,
      this.mediaIsHovered && CLASS_NAME_MEDIA_HOVERED,
      this.hasNoAdditionalInfo && CLASS_NAME_NO_ADDITIONAL_INFO,
      this.hasNoDigests && CLASS_NAME_NO_DIGESTS,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  get contextText() {
    const { user } = this.args;
    const currentUserCompanyId = this.session.currentUser.company?.id;
    const currentUserConnection = user.companySellerConnections.find(({ companyId }) => companyId == currentUserCompanyId);

    if (currentUserConnection?.lastHired) {
      const lastHiredHumanReadable = this.moment.moment(currentUserConnection.lastHired).fromNow();
      return `Last hired by you ${lastHiredHumanReadable}`;
    } else if (currentUserConnection?.invited) {
      return 'Invited by you';
    }

    return '';
  }

  get hasNoAdditionalInfo() {
    const { user } = this.args;
    return user.services.length === 0
      && user.experiences.length === 0
      && this.featuredBrands.length === 0;
  }

  get hasNoDigests() {
    return this.args.user.topFiveProjects.length === 0 || this.digestThumbnailError;
  }

  get hideRightInfoBlock() {
    const { user } = this.args;

    return this.media.isMediumAndDown
      && !this.hasNoAdditionalInfo
      && (
        !user.services.length
        || (!user.experiences.length && !user.workedWithBrands.length)
      )
  }

  get featuredBrands() {
    return this.args.user.workedWithBrands.reduce((accum, brand) => {
      // only show images that we did not generate
      if (!brand.image.isPlaceholderImage) {
        accum.push({
          src: brand.image.transforms.profile128,
          alt: brand.name,
          tooltip: brand.name,
          width: WORKED_WITH_BRAND_LOGO_SIZE_PX,
        });
      }

      return accum;
    }, []);
  }

  @action
  handleMediaMouseEnter() {
    this.mediaIsHovered = true;
  }

  @action
  handleMediaMouseLeave() {
    this.mediaIsHovered = false;
  }

  @action
  handleDigestImageError() {
    this.digestThumbnailError = true;
  }

  /**
   * Update UserConnection between the current and target user
   */
  @dropTask
  *toggleSave(user, e) {
    e.stopPropagation();
    e.preventDefault();

    const updatedSaveValue = !this.isSaved;

    // show change immediately
    this.isSaved = updatedSaveValue;

    try {
      yield this.companyConnectionCommands.updateNetworkConnection(
        this.session.currentUser.company,
        user,
        null,
        updatedSaveValue,
      );
    } catch (error) {
      // never actually saved, so revert internal state
      this.isSaved = !updatedSaveValue;

      this.applicationStateManager.addToast(`Failed saving ${user.fullName} to company network`);
      this.errorTracker.error('toggleSave - failed to save user', this, error);
      return;
    }

    try {
      yield this.userCommandsService.getUser(user.id);
    } catch (error) {
      this.applicationStateManager.addToast(`Failed to reload changes to ${user.fullName}`);
      this.errorTracker.error('toggleSave - failed to reload user', this, error);
    }
  }
}

export default PeopleCreatorCard;
